/**
 * useMatrixViews Hook
 * Manages saved matrix views including loading, saving, and switching
 *
 * Views are stored in:
 * - matrix_views: The view definitions (name, filters, public flag)
 * - matrix_view_preferences: Per-user preferences (order, visibility)
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

/**
 * Default "All" view - always present, shows everything
 */
const DEFAULT_VIEW = {
  id: 'all',
  name: 'All',
  isDefault: true,
  filters: {},
  isPublic: false,
  userId: null,
  authorName: 'System'
};

/**
 * Custom hook for managing matrix views
 *
 * @param {string} projectId - The project/space ID (null for global views)
 * @param {string} userId - The current user's ID
 * @param {Object} options - Configuration options
 * @param {string} options.companyId - Company ID (required for global views when projectId is null)
 * @param {number} options.autoSaveDelay - Auto-save debounce delay in ms (default: 1000)
 * @returns {Object} View state and actions
 */
export function useMatrixViews(projectId, userId, options = {}) {
  const { companyId, autoSaveDelay = 1000 } = options;
  const isGlobalView = !projectId && !!companyId;

  // State
  const [views, setViews] = useState([DEFAULT_VIEW]);
  const [activeViewId, setActiveViewId] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  // Refs for debouncing
  const autoSaveTimeoutRef = useRef(null);

  // Get the currently active view (memoized to prevent infinite loops)
  const activeView = useMemo(() => {
    return views.find(v => v.id === activeViewId) || DEFAULT_VIEW;
  }, [views, activeViewId]);

  // Check if user owns the current view
  const isViewOwner = activeView.userId === userId;

  // ============================================
  // Load views from database
  // ============================================
  const loadViews = useCallback(async () => {
    if (!userId || (!projectId && !companyId)) {
      setViews([DEFAULT_VIEW]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Fetch views - either for a specific project or global (company-level)
      let query = supabase
        .from('matrix_views')
        .select(`
          *,
          user_profiles!user_id (
            id,
            first_name,
            last_name,
            display_name
          )
        `)
        .or(`user_id.eq.${userId},is_public.eq.true`);

      if (projectId) {
        query = query.eq('project_id', projectId);
      } else {
        query = query.is('project_id', null).eq('company_id', companyId);
      }

      const { data: viewsData, error: viewsError } = await query;

      if (viewsError) throw viewsError;

      // Fetch user's view preferences
      const { data: prefsData, error: prefsError } = await supabase
        .from('matrix_view_preferences')
        .select('*')
        .eq('user_id', userId)
        .order('display_order', { ascending: true });

      if (prefsError) throw prefsError;

      // Build views array with preferences applied
      const loadedViews = (viewsData || [])
        .map(view => {
          const pref = prefsData?.find(p => p.view_id === view.id);
          const authorName = view.user_profiles?.display_name ||
            `${view.user_profiles?.first_name || ''} ${view.user_profiles?.last_name || ''}`.trim() ||
            'Unknown';

          return {
            id: view.id,
            name: view.name,
            filters: view.filters || {},
            isPublic: view.is_public,
            userId: view.user_id,
            authorName,
            isDefault: false,
            displayOrder: pref?.display_order ?? 999,
            isVisible: pref?.is_visible ?? true
          };
        })
        .filter(v => v.isVisible)
        .sort((a, b) => a.displayOrder - b.displayOrder);

      // Always include "All" view as first item
      setViews([DEFAULT_VIEW, ...loadedViews]);

    } catch (error) {
      console.error('[useMatrixViews] Error loading views:', error);
      toast.error('Failed to load saved views');
      setViews([DEFAULT_VIEW]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, projectId, companyId]);

  // Load views on mount and when dependencies change
  // Use a ref to prevent double-loading in StrictMode
  const loadedForRef = useRef({ userId: null, projectId: null, companyId: null });

  useEffect(() => {
    // Skip if already loaded for the same user/project/company
    if (loadedForRef.current.userId === userId && loadedForRef.current.projectId === projectId && loadedForRef.current.companyId === companyId) {
      return;
    }
    loadedForRef.current = { userId, projectId, companyId };
    loadViews();
  }, [loadViews, userId, projectId, companyId]);

  // ============================================
  // Save view filters
  // ============================================
  const saveViewFilters = useCallback(async (viewId, filters) => {
    if (viewId === 'all' || !userId) return;

    const view = views.find(v => v.id === viewId);
    if (!view || view.userId !== userId) return; // Can't save views you don't own

    try {
      setSavingStatus('saving');

      const { error } = await supabase
        .from('matrix_views')
        .update({
          filters: {
            activeFilters: filters.activeFilters || [],
            hiddenColumns: filters.hiddenColumns || [],
            hiddenRows: filters.hiddenRows || [],
            columnOrder: filters.columnOrder || [],
            rowOrder: filters.rowOrder || []
          },
          updated_date: new Date().toISOString()
        })
        .eq('id', viewId);

      if (error) throw error;

      // Update local state
      setViews(prev => prev.map(v =>
        v.id === viewId
          ? { ...v, filters }
          : v
      ));

      setSavingStatus('saved');

      // Reset status after a delay
      setTimeout(() => setSavingStatus(null), 2000);

    } catch (error) {
      console.error('[useMatrixViews] Error saving view:', error);
      setSavingStatus('error');
      toast.error('Failed to save view');
    }
  }, [views, userId]);

  // Auto-save with debounce
  const autoSaveFilters = useCallback((viewId, filters) => {
    // Clear any pending save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Schedule new save
    autoSaveTimeoutRef.current = setTimeout(() => {
      saveViewFilters(viewId, filters);
    }, autoSaveDelay);
  }, [saveViewFilters, autoSaveDelay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // ============================================
  // Create new view
  // ============================================
  const createView = useCallback(async (name, filters = {}, isPublic = false) => {
    if (!userId || (!projectId && !companyId)) {
      toast.error('Unable to create view');
      return null;
    }

    try {
      const insertData = {
        name,
        user_id: userId,
        filters,
        is_public: isPublic
      };

      if (projectId) {
        insertData.project_id = projectId;
      } else {
        insertData.company_id = companyId;
      }

      const { data, error } = await supabase
        .from('matrix_views')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      // Add to local state
      const newView = {
        id: data.id,
        name: data.name,
        filters: data.filters || {},
        isPublic: data.is_public,
        userId: data.user_id,
        authorName: 'You',
        isDefault: false,
        displayOrder: views.length,
        isVisible: true
      };

      setViews(prev => [...prev, newView]);
      setActiveViewId(data.id);

      toast.success(`View "${name}" created`);
      return data.id;

    } catch (error) {
      console.error('[useMatrixViews] Error creating view:', error);
      toast.error('Failed to create view');
      return null;
    }
  }, [userId, projectId, companyId, views.length]);

  // ============================================
  // Rename view
  // ============================================
  const renameView = useCallback(async (viewId, newName) => {
    if (viewId === 'all' || !userId) return false;

    const view = views.find(v => v.id === viewId);
    if (!view || view.userId !== userId) {
      toast.error('You can only rename your own views');
      return false;
    }

    try {
      const { error } = await supabase
        .from('matrix_views')
        .update({ name: newName, updated_date: new Date().toISOString() })
        .eq('id', viewId);

      if (error) throw error;

      setViews(prev => prev.map(v =>
        v.id === viewId ? { ...v, name: newName } : v
      ));

      toast.success('View renamed');
      return true;

    } catch (error) {
      console.error('[useMatrixViews] Error renaming view:', error);
      toast.error('Failed to rename view');
      return false;
    }
  }, [views, userId]);

  // ============================================
  // Delete view
  // ============================================
  const deleteView = useCallback(async (viewId) => {
    if (viewId === 'all') {
      toast.error('Cannot delete the default view');
      return false;
    }

    const view = views.find(v => v.id === viewId);
    if (!view || view.userId !== userId) {
      toast.error('You can only delete your own views');
      return false;
    }

    try {
      const { error } = await supabase
        .from('matrix_views')
        .delete()
        .eq('id', viewId);

      if (error) throw error;

      setViews(prev => prev.filter(v => v.id !== viewId));

      // If we deleted the active view, switch to "All"
      if (activeViewId === viewId) {
        setActiveViewId('all');
      }

      toast.success('View deleted');
      return true;

    } catch (error) {
      console.error('[useMatrixViews] Error deleting view:', error);
      toast.error('Failed to delete view');
      return false;
    }
  }, [views, userId, activeViewId]);

  // ============================================
  // Toggle view public/private
  // ============================================
  const toggleViewPublic = useCallback(async (viewId) => {
    if (viewId === 'all' || !userId) return false;

    const view = views.find(v => v.id === viewId);
    if (!view || view.userId !== userId) {
      toast.error('You can only modify your own views');
      return false;
    }

    try {
      const newIsPublic = !view.isPublic;

      const { error } = await supabase
        .from('matrix_views')
        .update({ is_public: newIsPublic, updated_date: new Date().toISOString() })
        .eq('id', viewId);

      if (error) throw error;

      setViews(prev => prev.map(v =>
        v.id === viewId ? { ...v, isPublic: newIsPublic } : v
      ));

      toast.success(newIsPublic ? 'View is now public' : 'View is now private');
      return true;

    } catch (error) {
      console.error('[useMatrixViews] Error toggling view visibility:', error);
      toast.error('Failed to update view');
      return false;
    }
  }, [views, userId]);

  // ============================================
  // Reorder views
  // ============================================
  const reorderViews = useCallback(async (orderedViewIds) => {
    if (!userId) return false;

    try {
      // Update preferences for each view
      const updates = orderedViewIds.map((viewId, index) => ({
        user_id: userId,
        view_id: viewId,
        display_order: index,
        is_visible: true
      }));

      const { error } = await supabase
        .from('matrix_view_preferences')
        .upsert(updates, { onConflict: 'user_id,view_id' });

      if (error) throw error;

      // Update local state
      const viewMap = new Map(views.map(v => [v.id, v]));
      const reorderedViews = [
        DEFAULT_VIEW,
        ...orderedViewIds
          .filter(id => id !== 'all')
          .map(id => viewMap.get(id))
          .filter(Boolean)
      ];

      setViews(reorderedViews);
      return true;

    } catch (error) {
      console.error('[useMatrixViews] Error reordering views:', error);
      toast.error('Failed to reorder views');
      return false;
    }
  }, [views, userId]);

  // ============================================
  // Duplicate view
  // ============================================
  const duplicateView = useCallback(async (viewId) => {
    const view = views.find(v => v.id === viewId);
    if (!view) return null;

    return createView(
      `${view.name} (copy)`,
      { ...view.filters },
      false // Always create as private
    );
  }, [views, createView]);

  return {
    // State
    views,
    activeViewId,
    activeView,
    isLoading,
    savingStatus,
    isViewOwner,

    // Actions
    setActiveViewId,
    loadViews,
    saveViewFilters,
    autoSaveFilters,
    createView,
    renameView,
    deleteView,
    toggleViewPublic,
    reorderViews,
    duplicateView
  };
}

export default useMatrixViews;
